import * as joda from "@js-joda/core";
import * as c from "purify-ts/Codec";
import h from "trader-hyperscript";
import { makeEncoderFromCodec, makeRoute } from "trader.ts/route";
import { Source } from "trader.ts/types/source";
import { dyn, dynStyle } from "trader.ts/ui";
import { checkAllCasesHandled, mapPartial } from "trader.ts/utils";
import {
  formulaid,
  instrumentid,
  internalteacherid,
  lessongroupid,
  planningmode,
  roomid,
  siteid,
  teacherid,
} from "../../../sql/classy/types";
import { renderWeekCalendar } from "../../client/renderweekcalendar";
import { capitalize } from "../../lib/capitalize";
import { pinkPlanningColors } from "../../lib/colors";
import { groupByIntoArrays } from "../../lib/groupbyintoarrays";
import { isNil } from "../../lib/istype";
import { allWeekdays } from "../../lib/time";
import { parseWeekday } from "../../parsing_from_ur";
import { HHmm, ddMM, renderFirstNameLastName } from "../../render";
import * as routes from "../../routes";
import { translateWeekday } from "../../translations_specific";
import * as FA from "./../../../styles/fontawesome.css.classes";
import * as style from "./../../../styles/style.css.classes";
import { makeClientsideRouter } from "./../../client/clientsiderouter";

export type grouplessonToShow = {
  lessongroupid: lessongroupid;
  time: joda.LocalTime;
  weekday: string;
  instrumentid: instrumentid;
  teacher_firstname: string;
  teacher_lastname: string;
  formula_description: string;
  formula_comments: string;
  formula_lessonduration: number;
  next_lesson_date: joda.LocalDate | null;
  enrolled_student_count: number;
  max_number_of_students: number;
  totallessons: number;
  siteid: siteid | null;
  description: string | null;
  allow_trial_lessons: boolean;
  students_can_only_make_trial_lessons: boolean;
};

export type freeSlot = {
  date: joda.LocalDate;
  time: joda.LocalTime;
  duration: number;
  weekday: joda.DayOfWeek;
  siteid: siteid | null;
  freeForSingleLessonOnly: boolean;
};

export type teacherForPublicPlanning = {
  internalteacherid: internalteacherid;
  id: teacherid;
  firstname: string;
  lastname: string;
  instruments: instrumentid[];
  allTeachingPeriods: {
    time: joda.LocalTime;
    duration: number;
    weekday: joda.DayOfWeek;
    location: string;
    roomid: roomid | null;
    siteid: siteid | null;
  }[];
  freeSlotsThisWeek: freeSlot[];
  freeSlotsNextWeek: freeSlot[];
  freeSlotsThirdWeek: freeSlot[];
};

const siteIdC = routes.newtypeCodec<siteid>();
const instrumentIdC = routes.newtypeCodec<instrumentid>();
const internalTeacherIdC = routes.newtypeCodec<internalteacherid>();
const formulaIdC = routes.newtypeCodec<formulaid>();

const siteChoiceC = c.oneOf([
  c.Codec.interface({ tag: c.exactly("nopreference") }),
  c.Codec.interface({ tag: c.exactly("preference"), siteId: siteIdC }),
]);

type siteChoice = c.GetType<typeof siteChoiceC>;

const modeS = c.oneOf([
  c.Codec.interface({ tag: c.exactly("site") }),
  c.Codec.interface({ tag: c.exactly("instrument"), site: siteChoiceC }),
  c.Codec.interface({
    tag: c.exactly("private_or_group"),
    site: siteChoiceC,
    instrument: instrumentIdC,
  }),
  c.Codec.interface({
    tag: c.exactly("private_teacher"),
    site: siteChoiceC,
    instrument: instrumentIdC,
  }),
  c.Codec.interface({
    tag: c.exactly("grouplessons"),
    site: siteChoiceC,
    instrument: instrumentIdC,
  }),
  c.Codec.interface({
    tag: c.exactly("privatelessonsformula"),
    site: siteChoiceC,
    instrument: instrumentIdC,
    internalteacherid: c.nullable(internalTeacherIdC),
  }),
  c.Codec.interface({
    tag: c.exactly("privatelessonscalendar"),
    site: siteChoiceC,
    instrument: instrumentIdC,
    internalteacherid: c.nullable(internalTeacherIdC),
    formulaid: formulaIdC,
  }),
]);

type mode = c.GetType<typeof modeS>;
const modeRoute = makeRoute("/mode/{mode:mode}", {
  mode: makeEncoderFromCodec(modeS),
});

const clientsideRouter = makeClientsideRouter(modeRoute, { tag: "site" });
const makeHashLink = clientsideRouter.makeHashLink;

type formula = {
  id: formulaid;
  type_: string;
  lessonduration: number;
  numberoflessons: number;
  startsfromperiod: boolean;
  planningmode: planningmode;
  description: string;
  comments: string;
  showonpublicplanning: boolean;
  instruments: { instrumentid: instrumentid }[];
};

export type planningDateKind =
  | { tag: "open" }
  | { tag: "holiday"; description: string }
  | { tag: "outsideperiod" };

type renderOpts = {
  schoolname: string;
  groupLessonsToShow: grouplessonToShow[];
  teachersForPublicPlanning: teacherForPublicPlanning[];
  sites: { id: siteid; description: string }[];
  instruments: { id: instrumentid; description: string }[];
  formulas: formula[];
  firstweek: { date: joda.LocalDate; kind: planningDateKind }[];
  secondweek: { date: joda.LocalDate; kind: planningDateKind }[];
  thirdweek: { date: joda.LocalDate; kind: planningDateKind }[];
  schoolhours: [joda.LocalTime, joda.LocalTime];
  waitingListActive: {
    private_waitinglists: instrumentid[];
    group_waitinglists: formulaid[];
  };
  allowPrivateTeacherSelection: boolean;
};

const invalidCardStyle = `
  background-color: var(--grey-100);
  border: 1px solid var(--grey-300);
  color: var(--grey-700);
  box-shadow: none;
  pointer-events: none;
`;

export function render(opts: renderOpts): HTMLElement | HTMLElement[] {
  const modeS = clientsideRouter.start();

  const instrumentIdsWithPrivateFormulas = opts.instruments
    .filter(function (instr) {
      return opts.formulas.some(
        (f) =>
          f.type_.startsWith("Private") &&
          f.instruments.some((fi) => fi.instrumentid === instr.id),
      );
    })
    .map((i) => i.id);

  const siteChoiceIds: siteid[] = (function () {
    if (opts.sites.length === 1) {
      return [opts.sites[0].id];
    }

    return opts.sites
      .map((s) => s.id)
      .filter((siteId) => {
        return (
          opts.teachersForPublicPlanning.some(
            (t) =>
              t.freeSlotsThisWeek.some(
                (sl) => isNil(sl.siteid) || sl.siteid === siteId,
              ) ||
              t.freeSlotsNextWeek.some(
                (sl) => isNil(sl.siteid) || sl.siteid === siteId,
              ) ||
              t.freeSlotsThirdWeek.some(
                (sl) => isNil(sl.siteid) || sl.siteid === siteId,
              ),
          ) ||
          opts.groupLessonsToShow.some(
            (l) => isNil(l.siteid) || l.siteid === siteId,
          )
        );
      });
  })();

  const showSitesLink = siteChoiceIds.length > 1;

  const header = (
    <header className={style.header_large_top}>
      <h1>{opts.schoolname}</h1>
      <h2>{"Inschrijven"}</h2>
    </header>
  );

  const navCollapsedS = new Source(false);

  const nav = dyn(modeS, (mode) =>
    renderNav(
      mode,
      opts,
      {
        showSitesLink,
        showGroupAndPrivateChoice: false,
      },
      navCollapsedS,
    ),
  );

  const main = dyn(modeS, function (mode) {
    if (
      opts.groupLessonsToShow.length === 0 &&
      opts.teachersForPublicPlanning.length === 0
    ) {
      return (
        <main style={mainStyle}>
          We hebben helaas geen plaatsen meer vrij om in te schrijven, sorry!
        </main>
      );
    }
    if (mode.tag === "site") {
      return renderSiteChoice(siteChoiceIds, opts.sites);
    } else if (mode.tag === "instrument") {
      return renderInstrumentChoice(
        opts.instruments,
        instrumentIdsWithPrivateFormulas,
        opts.formulas,
        opts.groupLessonsToShow,
        opts.teachersForPublicPlanning,
        mode.site,
        opts.waitingListActive,
      );
    } else {
      return renderAfterInstrument(mode, opts, {
        instrumentIdsWithPrivateFormulas,
      });
    }
  });

  return [header, ...nav, ...main];
}

function renderAfterInstrument(
  mode: mode,
  opts: renderOpts,
  {
    instrumentIdsWithPrivateFormulas,
  }: { instrumentIdsWithPrivateFormulas: number[] },
) {
  if (mode.tag === "site" || mode.tag === "instrument") {
    throw new Error("unreachable");
  }

  const instrument = opts.instruments.find(
    (instr) => instr.id === mode.instrument,
  );

  const siteFilter = mkSiteFilter(mode.site);

  const siteHasPrivateTeachersForInstrument =
    opts.teachersForPublicPlanning.some(
      (t) =>
        t.allTeachingPeriods.some(siteFilter) &&
        t.instruments.includes(mode.instrument),
    );

  const filteredGroupLessons = opts.groupLessonsToShow
    .filter(siteFilter)
    .filter((gl) => gl.instrumentid === mode.instrument);

  const instrumentHasPrivateFormulas =
    instrumentIdsWithPrivateFormulas.includes(mode.instrument);

  const showGroup = filteredGroupLessons.length > 0;
  const showPrivate =
    (siteHasPrivateTeachersForInstrument && instrumentHasPrivateFormulas) ||
    opts.waitingListActive.private_waitinglists.includes(mode.instrument);

  if (mode.tag === "private_or_group") {
    if (!showGroup && !showPrivate) {
      return (
        <main style={mainStyle}>
          <div>
            <h2>
              {"Sorry, we hebben op dit moment geen plaatsen vrij voor" +
                " " +
                instrument?.description}
            </h2>
          </div>
        </main>
      );
    } else if (showGroup && !showPrivate) {
      replaceHash(
        modeRoute.link({
          mode: { ...mode, tag: "grouplessons" },
        }),
      );
      return <span></span>;
    } else if (!showGroup && showPrivate) {
      replaceHash(
        modeRoute.link({
          mode: {
            ...mode,
            tag: "private_teacher",
          },
        }),
      );
      return <span></span>;
    } else {
      return (
        <main style={mainStyle}>
          <div>
            <h2>{renderHeader(opts.instruments, mode)}</h2>
          </div>
          <div className={style.grid_card_small}>
            {renderCard(
              makeHashLink({ ...mode, tag: "private_teacher" }),
              "Privélessen",
            )}
            {renderCard(
              makeHashLink({ ...mode, tag: "grouplessons" }),
              "Groepslessen",
            )}
          </div>
        </main>
      );
    }
  } else if (mode.tag === "private_teacher") {
    return renderPrivateTeacherChoice(
      opts.formulas,
      opts.allowPrivateTeacherSelection,
      mode,
      instrument!,
      opts.teachersForPublicPlanning,
    );
  } else if (mode.tag === "grouplessons") {
    return renderGrouplessonsChoice(mode, filteredGroupLessons);
  } else if (mode.tag === "privatelessonsformula") {
    return renderPrivateLessonsFormulas(
      mode,
      opts.teachersForPublicPlanning,
      opts.formulas,
      opts.instruments,
    );
  } else if (mode.tag === "privatelessonscalendar") {
    return renderPrivateLessonsCalendar(
      mode,
      opts.teachersForPublicPlanning,
      opts.formulas,
      opts.instruments,
      opts.schoolhours,
    );
  } else {
    return checkAllCasesHandled(mode);
  }

  function renderGrouplessonsChoice(
    mode: {
      tag: "grouplessons";
      site: siteChoice;
      instrument: instrumentid;
    },
    groupLessonsToShow: grouplessonToShow[],
  ) {
    function renderGroupLesson(gl: grouplessonToShow) {
      const lgHasSpace = gl.enrolled_student_count < gl.max_number_of_students;
      function renderLinkCard(label: string, link: string): HTMLElement {
        return (
          <a
            style="min-width: auto; min-height: auto; font-weight: 600; margin-left: 16px;"
            className={style.card + " " + style.card_accent}
            href={link}
          >
            {label}
          </a>
        );
      }

      return (
        <section style="width:100%; display:inline-flex; flex-direction:column; align-items:end;">
          <div
            style="display: block; width: 100%; border: 1px solid var(--grey-400);"
            className={style.card}
          >
            <ul style="">
              <li className={style.text_with_icon}>
                <i className={FA.icon_clock_regular} style="color: black"></i>
                <div>
                  <b>
                    {"%totallessons lessen op %weekday om %starttime tot %endtime"
                      .replace("%totallessons", gl.totallessons.toString())
                      .replace(
                        "%weekday",
                        translateWeekday(
                          parseWeekday(gl.weekday),
                        ).toLowerCase(),
                      )
                      .replace("%starttime", gl.time.format(HHmm))
                      .replace(
                        "%endtime",
                        gl.time
                          .plusMinutes(gl.formula_lessonduration)
                          .format(HHmm),
                      )}
                  </b>
                </div>
              </li>

              <li className={style.text_with_icon}>
                <i className={FA.icon_glasses} style="color: black"></i>
                <div>
                  <b>
                    {"Bij %teacherfirstname %teacherlastname"
                      .replace("%teacherfirstname", gl.teacher_firstname)
                      .replace("%teacherlastname", gl.teacher_lastname)}
                  </b>
                </div>
              </li>

              <li className={style.text_with_icon}>
                <i className={FA.icon_clipboard_solid}></i>
                <div>
                  {gl.formula_description} -{" "}
                  {opts.instruments.find((i) => i.id === gl.instrumentid)
                    ?.description || ""}
                </div>
              </li>

              {gl.formula_comments ? (
                <li className={style.text_with_icon}>
                  <i className={FA.icon_clipboard_solid}></i>
                  <div>{gl.formula_comments}</div>
                </li>
              ) : (
                <span></span>
              )}

              <li className={style.text_with_icon}>
                <div>
                  <i className={FA.icon_user_plus_solid}></i>
                  {gl.max_number_of_students === 2
                    ? "Duolessen"
                    : "Groepslessen"}
                </div>
              </li>

              <li className={style.text_with_icon}>
                <i className={FA.icon_arrow_right}></i>
                <div>
                  {"Volgende les op"}{" "}
                  <b>{gl.next_lesson_date?.format(ddMM) || ""}</b>
                </div>
              </li>

              {gl.description !== null ? (
                <li className={style.text_with_icon} style="font-style: italic">
                  <i className={FA.icon_paperclip_solid}></i>
                  <div>{gl.description}</div>
                </li>
              ) : (
                <div></div>
              )}
            </ul>

            <div
              style="display: flex; align-items: stretch; justify-content: flex-end; margin-top: 16px;"
              className={style.cancel_card_padding}
            >
              {lgHasSpace
                ? [
                    gl.allow_trial_lessons &&
                    gl.students_can_only_make_trial_lessons
                      ? null
                      : renderLinkCard(
                          gl.allow_trial_lessons
                            ? "Volledige cursus" // Volledige cursus vs Proefles is duidelijker
                            : "Schrijf je in",
                          routes.public_new_enrollment_lessongroupRoute.link({
                            lessongroupid: gl.lessongroupid,
                            trial_lesson: false,
                          }),
                        ),
                    gl.allow_trial_lessons
                      ? renderLinkCard(
                          "Proefles",
                          routes.public_new_enrollment_lessongroupRoute.link({
                            lessongroupid: gl.lessongroupid,
                            trial_lesson: true,
                          }),
                        )
                      : null,
                  ].compact()
                : [
                    renderLinkCard(
                      "Wachtlijst",
                      routes.waitinglist_studentRoute.link({
                        privateorgroup: {
                          tag: "lessongroup",
                          lessongroupid: gl.lessongroupid,
                        },
                      }),
                    ),
                  ]}
            </div>
          </div>
        </section>
      );
    }

    return (
      <main style={mainStyle}>
        <div>
          <h2>{renderHeader(opts.instruments, mode)}</h2>
        </div>
        <div>
          {groupByIntoArrays(groupLessonsToShow, (gl) => gl.weekday)
            .orderBy(
              ([_, group]) => parseWeekday(group[0].weekday).ordinal(),
              "asc",
            )
            .map(function ([_, group]) {
              const rendered = group
                .orderBy(
                  [
                    (g) => g.next_lesson_date?.toEpochDay() || "",
                    (g) => g.time.toSecondOfDay(),
                    (g) => g.teacher_firstname + " " + g.teacher_lastname,
                  ],
                  ["asc", "asc", "asc"],
                )
                .map(renderGroupLesson);
              return (
                <article>
                  <h3 style="margin-bottom: 8px; font-weight: 600; font-size: 1rem;">
                    {translateWeekday(parseWeekday(group[0].weekday))}
                  </h3>
                  <div style="margin-bottom: 32px">{rendered}</div>
                </article>
              );
            })}
        </div>
      </main>
    );
  }

  function renderPrivateTeacherChoice(
    formulas: formula[],
    allowPrivateTeacherSelection: boolean,
    mode: {
      tag: "private_teacher";
      site: siteChoice;
      instrument: instrumentid;
    },
    instrument: { description: string },
    teachers_: teacherForPublicPlanning[],
  ) {
    const formulaFilter = mkPrivateFormulaFilter(formulas, mode.instrument);
    const teachers = teachers_
      .filter(
        (t) =>
          t.allTeachingPeriods.some(siteFilter) &&
          t.instruments.includes(mode.instrument),
      )
      .map((t) => ({
        ...t,
        hasFreeSlots:
          t.freeSlotsThisWeek
            .concat(t.freeSlotsNextWeek)
            .concat(t.freeSlotsThirdWeek)
            .filter(siteFilter)
            .filter(formulaFilter).length > 0,
      }));
    const allTeachersOccupied = teachers.every((t) => t.hasFreeSlots === false);

    if (!allTeachersOccupied && !allowPrivateTeacherSelection) {
      // Redirect immediately to "no preference"
      replaceHash(
        modeRoute.link({
          mode: {
            tag: "privatelessonsformula",
            internalteacherid: null,
            instrument: mode.instrument,
            site: mode.site,
          },
        }),
      );
    }

    return (
      <main style={mainStyle}>
        <div>
          <h2>{renderHeader(opts.instruments, mode)}</h2>
        </div>
        {allowPrivateTeacherSelection ? (
          <section className={style.grid_card_small}>
            {renderCard(
              makeHashLink({
                tag: "privatelessonsformula",
                internalteacherid: null,
                instrument: mode.instrument,
                site: mode.site,
              }),
              "Geen voorkeur",
              allTeachersOccupied ? invalidCardStyle : "",
            )}
            {teachers.map((t) =>
              renderCard(
                makeHashLink({
                  tag: "privatelessonsformula",
                  internalteacherid: t.internalteacherid,
                  instrument: mode.instrument,
                  site: mode.site,
                }),
                t.firstname + " " + t.lastname,
                t.hasFreeSlots ? "" : invalidCardStyle,
              ),
            )}
          </section>
        ) : (
          []
        )}

        {allTeachersOccupied ? (
          <article style="font-style: italic">
            {"Op dit moment hebben onze docenten geen plaatsen meer vrij voor privéles" +
              " " +
              instrument.description}
          </article>
        ) : (
          <span></span>
        )}
        {opts.waitingListActive.private_waitinglists.some(
          (instrid) => mode.instrument === instrid,
        )
          ? renderCard(
              routes.waitinglist_studentRoute.link({
                privateorgroup: {
                  tag: "private",
                  instrumentid: mode.instrument,
                },
              }),
              <div className={style.text_with_icon}>
                <i className={FA.icon_clipboard_solid}></i>
                <div style="text-decoration: none; font-weight: 600;">
                  {"Zet me op de wachtlijst"}
                </div>
              </div>,
              ` width: 100%;
            text-align: center;
            justify-content: center;
            display: inline-flex;
            align-items: center;
            min-height: 56px;
            margin-top: 24px;
`,
            )
          : []}
      </main>
    );
  }
}

function renderNav(
  mode: mode,
  renderOpts: renderOpts,
  opts: {
    showSitesLink: boolean;
    showGroupAndPrivateChoice: boolean;
  },
  navCollapsedS: Source<boolean>,
) {
  const previousChoices = calcPreviousChoices();
  const currentChoice = calcCurrentChoice();

  const previousChoicesRendered = previousChoices.map((c) => (
    <li>
      <a href={makeHashLink(c.link)}>
        {c.label}: {c.choice}
      </a>
    </li>
  ));
  const previousChoicesRenderedContainer = dynStyle(
    navCollapsedS,
    (c) => (c === true ? "display: none;" : ""),
    <ul>{previousChoicesRendered}</ul>,
  );

  const chevron = dynStyle(
    navCollapsedS,
    (c) =>
      c === true ? "transform: rotate(270deg)" : "transform: rotate(90deg)",
    <i className={FA.icon_arrow_left}></i>,
  );

  return (
    <nav
      className={style.nav_large_top_public}
      onclick={() => navCollapsedS.set(!navCollapsedS.get())}
    >
      {previousChoicesRenderedContainer}
      <h3>
        <div>{currentChoice}</div>
        {chevron}
      </h3>
    </nav>
  );

  function calcPreviousChoices(): {
    link: mode;
    label: string;
    choice: string;
  }[] {
    const site =
      opts.showSitesLink && "site" in mode
        ? {
            link: { tag: "site" as const },
            label: "Vestiging",
            choice:
              mode.site.tag === "nopreference"
                ? "Geen voorkeur"
                : renderOpts.sites.find(
                    (s) =>
                      mode.site.tag === "preference" &&
                      s.id === mode.site.siteId,
                  )?.description || "",
          }
        : null;

    const instrument =
      "instrument" in mode
        ? {
            link: { tag: "instrument" as const, site: mode.site },
            label: "Cursus",
            choice:
              renderOpts.instruments.find((i) => i.id === mode.instrument)
                ?.description || "",
          }
        : null;

    const teacher =
      "internalteacherid" in mode
        ? {
            link: {
              tag: "private_teacher" as const,
              site: mode.site,
              instrument: mode.instrument,
            },
            label: "Docent",
            choice:
              mode.internalteacherid === null
                ? "Geen voorkeur"
                : (() => {
                    const found = renderOpts.teachersForPublicPlanning.find(
                      (t) => t.internalteacherid === mode.internalteacherid,
                    );
                    return found ? renderFirstNameLastName(found) : "";
                  })(),
          }
        : null;

    const formula =
      "formulaid" in mode
        ? {
            link: {
              tag: "privatelessonsformula" as const,
              site: mode.site,
              instrument: mode.instrument,
              internalteacherid: mode.internalteacherid,
            },
            label: "Formule",
            choice:
              renderOpts.formulas.find((f) => f.id === mode.formulaid)
                ?.description || "",
          }
        : null;

    return [site, instrument, teacher, formula].compact();
  }
  function calcCurrentChoice() {
    if (mode.tag === "site") {
      return "Vestiging";
    } else if (mode.tag === "instrument") {
      return "Cursus";
    } else if (mode.tag === "private_or_group") {
      return "Privé of groep";
    } else if (mode.tag === "grouplessons") {
      return "Groepslessen";
    } else if (mode.tag === "private_teacher") {
      return "Docent";
    } else if (mode.tag === "privatelessonsformula") {
      return "Formule";
    } else if (mode.tag === "privatelessonscalendar") {
      return "Tijdstip";
    } else {
      return checkAllCasesHandled(mode);
    }
  }
}

export function planningStartPublicModeLink(m: mode) {
  return routes.planningStartPublicRoute.link() + makeHashLink(m);
}

const mainStyle =
  "max-width: 800px; padding: 32px 16px; background-color: transparent";

function replaceHash(s: string) {
  // Replacing location when we redirect so we don't get two History entries that you can never go back to
  const baseUrl = window.location.href.split("#")[0];
  window.location.replace(baseUrl + "#" + s);
}

function renderSiteChoice(
  siteChoiceIds: siteid[],
  sites: { id: siteid; description: string }[],
) {
  if (siteChoiceIds.length <= 1) {
    replaceHash(
      modeRoute.link({
        mode: { tag: "instrument", site: { tag: "nopreference" } },
      }),
    );
    return <span></span>;
  }

  const siteChoices = mapPartial(
    siteChoiceIds,
    (id) => sites.find((s) => s.id === id) || null,
  );

  return (
    <main style={mainStyle}>
      <div>
        <h2>{renderHeader([], { tag: "site" })}</h2>
      </div>
      <div className={style.grid_card_small}>
        {renderCard(
          makeHashLink({ tag: "instrument", site: { tag: "nopreference" } }),
          "Geen voorkeur",
        )}
        {siteChoices.map((site) =>
          renderCard(
            makeHashLink({
              tag: "instrument",
              site: { tag: "preference", siteId: site.id },
            }),
            site.description,
          ),
        )}
      </div>
    </main>
  );
}

function renderInstrumentChoice(
  instruments: { id: instrumentid; description: string }[],
  instrumentIdsWithPrivateFormulas: instrumentid[],
  allFormulas: formula[],
  groupLessons: grouplessonToShow[],
  teachers: teacherForPublicPlanning[],
  siteChoice: siteChoice,
  waitingListActive: {
    private_waitinglists: instrumentid[];
    group_waitinglists: formulaid[];
  },
) {
  const siteFilter = mkSiteFilter(siteChoice);
  const allInstrumentIdsFromGroupLessons = groupLessons
    .filter(siteFilter)
    .map((gl) => gl.instrumentid);

  const idsToShow = allInstrumentIdsFromGroupLessons.concat(
    teachers.flatMap((t) => t.instruments),
  );

  const allInstrumentIdsWithWaitinglist = instruments
    .filter((instr) =>
      waitingListActive.private_waitinglists.some(
        (instrid) => instrid === instr.id,
      ),
    )
    .map((instr) => instr.id);

  const idsToShowValidFromTeachersPrivate = teachers
    .flatMap((t) => {
      const openThisWeek = t.freeSlotsThisWeek.filter(siteFilter);
      const openNextWeek = t.freeSlotsNextWeek.filter(siteFilter);
      const openThirdWeek = t.freeSlotsThirdWeek.filter(siteFilter);
      const allSlots = openThisWeek.concat(openNextWeek).concat(openThirdWeek);
      return t.instruments.filter((instr) => {
        // show every instrument that has at least one formula that fits
        // in at least one slot
        const formulaFilter = mkPrivateFormulaFilter(allFormulas, instr);
        return allSlots.some(formulaFilter);
      });
    })
    .filter((i) => instrumentIdsWithPrivateFormulas.includes(i));

  const idsToShowValid = allInstrumentIdsFromGroupLessons
    .concat(allInstrumentIdsWithWaitinglist)
    .concat(idsToShowValidFromTeachersPrivate);

  return (
    <main style={mainStyle}>
      <div>
        <h2>
          {renderHeader(instruments, { tag: "instrument", site: siteChoice })}
        </h2>
      </div>
      <div className={style.grid_card_small}>
        {instruments
          // We don't show instruments at all that don't have any teachers (or grouplessons) assigned to it
          .filter((instr) => idsToShow.includes(instr.id))
          .orderBy((instr) => instr.description)
          .map((instr) => {
            const valid = idsToShowValid.includes(instr.id);
            return renderCard(
              makeHashLink({
                tag: "private_or_group",
                site: siteChoice,
                instrument: instr.id,
              }),
              instr.description,
              valid ? "" : invalidCardStyle,
            );
          })}
      </div>
    </main>
  );
}

function renderHeader(
  instruments: { id: number; description: string }[],
  s: mode,
) {
  if (s.tag === "site") {
    return "In welke vestiging wil je les volgen?";
  }
  if (s.tag === "instrument") {
    return "Welke cursus wil je volgen?";
  }
  if (s.tag === "private_or_group") {
    const instr = instruments.find((i) => i.id === s.instrument);
    return (
      "Zoek je privélessen of groepslessen" +
      " " +
      (instr?.description?.toLowerCase() || "") +
      "?"
    );
  }
  if (s.tag === "private_teacher") {
    const instr = instruments.find((i) => i.id === s.instrument);
    const lessontext =
      instr && instr.description.toLowerCase().indexOf("priv") > -1
        ? "les"
        : "privéles";
    return "Bij welke docent wil je %lesson %instr volgen?"
      .replace("%lesson", lessontext)
      .replace("%instr", instr?.description?.toLowerCase() || "");
  }
  if (s.tag === "grouplessons") {
    const instr = instruments.find((i) => i.id === s.instrument);
    return (
      "Onze groepslessen" +
      " " +
      (instr?.description?.toLowerCase() || "")
    );
  }
  if (s.tag === "privatelessonsformula") {
    return "Welke formule wil je volgen?";
  }
  if (s.tag === "privatelessonscalendar") {
    return "Beschikbare tijdstippen";
  }
  return checkAllCasesHandled(s);
}

export function renderCard(
  link: string,
  text: string | HTMLElement,
  st?: string,
): HTMLElement {
  return (
    <a href={link} style={st || ""} className={style.card}>
      <b>{text}</b>
    </a>
  );
}

function renderPrivateLessonsCalendar(
  mode: {
    tag: "privatelessonscalendar";
    site: siteChoice;
    instrument: instrumentid;
    internalteacherid: internalteacherid | null;
    formulaid: formulaid;
  },
  allTeachers: teacherForPublicPlanning[],
  allFormulas: formula[],
  instruments: { id: instrumentid; description: string }[],
  schoolhours: [joda.LocalTime, joda.LocalTime],
) {
  const siteFilter = mkSiteFilter(mode.site);
  const formula = allFormulas.find((f) => f.id === mode.formulaid);
  if (!formula) {
    throw new Error("Formula not found");
  }

  const allSlots: freeSlot[] = getSlotsForFormula(
    mode,
    allTeachers,
    formula,
  ).filter(siteFilter);

  const dateFormat = joda.DateTimeFormatter.ofPattern("dd/MM");
  const timeFormat = joda.DateTimeFormatter.ofPattern("HH:mm");

  const slotsGroupedByWeekday = allSlots.groupBy((sl) =>
    sl.date.dayOfWeek().ordinal(),
  );

  const renderedSlotsByWeekday = mapPartial(allWeekdays, (wd) =>
    renderSlotsByDate(wd, slotsGroupedByWeekday),
  );

  const renderedSlotsOnCalendar = renderSlotsOnWeekCalendar(
    schoolhours,
    allSlots,
  );

  return (
    <main style="max-width: 1600px;">
      <article style={"max-width: 800px; margin: 0px auto;"}>
        <h2>{renderHeader(instruments, mode)}</h2>
      </article>

      <div className={style.only_on_phone}>
        <div className={style.grid_card_small}>{renderedSlotsByWeekday}</div>
      </div>
      <div className={style.remove_on_phone}>{renderedSlotsOnCalendar}</div>
    </main>
  );

  function renderSlotsOnWeekCalendar(
    schoolhours: [joda.LocalTime, joda.LocalTime],
    slots: freeSlot[],
  ): HTMLElement | HTMLElement[] {
    const hours = calculateHours();

    return renderWeekCalendar({
      hours,
      alwaysShowGroups: false,
      collapseEmptyTeachers: false, // doesn't matter
      weekdays: allWeekdays.map((wd) => [
        wd,
        {
          label: capitalize(translateWeekday(wd)).substring(0, 2),
          contents: null,
        },
      ]),
      groupByTeachers: [
        // TODO?
        { internalteacherid: -1 as internalteacherid, firstname: "" },
      ],
      slots: slots.map((sl) => ({
        ...sl,
        internalteacherid: -1 as internalteacherid, //todo ?
        colors: pinkPlanningColors,
        // This difference in colors was not clear at all what it meant
        // colors: sl.freeForSingleLessonOnly
        //   ? greyPlanningColors
        //   : pinkPlanningColors,
        wd: sl.date.dayOfWeek(),
        contents:
          sl.time.format(timeFormat) +
          " (" +
          (formula!.numberoflessons > 1 ? "vanaf" + " " : "") +
          sl.date.format(dateFormat) +
          ")",
        onclick: {
          href: routes.public_new_enrollment_privateRoute.link({
            instrumentid: mode.instrument,
            internalteacherid:
              mode.internalteacherid === null
                ? undefined
                : mode.internalteacherid,
            formulaid: mode.formulaid,
            starttime: sl.time,
            startdate: sl.date,
          }),
        },
      })),
      style: { headerStickyPosition: 0 },
    });

    function calculateHours(): [number, number] {
      const earliestStart = slots
        .map((sl) => sl.time.toSecondOfDay())
        .minBy((s) => s);
      const latestEnd = slots
        .map((sl) => sl.time.plusMinutes(sl.duration).toSecondOfDay())
        .maxBy((s) => s);
      const startSeconds = [
        earliestStart || schoolhours[0].toSecondOfDay(),
        schoolhours[0].toSecondOfDay(),
      ].maxBy((s) => s)!;
      const endSeconds = [
        latestEnd || schoolhours[1].toSecondOfDay(),
        schoolhours[1].toSecondOfDay(),
      ].minBy((s) => s)!;
      const startTime = joda.LocalTime.ofSecondOfDay(startSeconds);
      const startHour = startTime.hour();
      const endTime = joda.LocalTime.ofSecondOfDay(endSeconds);
      const endHour =
        endTime.minute() > 0 ? endTime.hour() + 1 : endTime.hour();

      const expandHoursSome = endHour - startHour <= 6;

      return expandHoursSome
        ? [startHour - 1, endHour + 1]
        : [startHour, endHour];
    }
  }

  function renderSlotsByDate(
    wd: joda.DayOfWeek,
    slotsGroupedByDate: { [key: string]: freeSlot[] },
  ): HTMLElement | null {
    // Rendering days with slots inside
    const slotsThisDay = slotsGroupedByDate[wd.ordinal()] || [];
    if (slotsThisDay.length === 0) {
      return null;
    }
    return (
      <div>
        <h2>{translateWeekday(wd)}</h2>
        <div>
          {slotsThisDay
            .orderBy((sl) => sl.time.toSecondOfDay())
            .map((sl) => (
              <a
                className={style.card}
                style="margin-bottom: 8px"
                href={routes.public_new_enrollment_privateRoute.link({
                  instrumentid: mode.instrument,
                  internalteacherid:
                    mode.internalteacherid === null
                      ? undefined
                      : mode.internalteacherid,
                  formulaid: mode.formulaid,
                  starttime: sl.time,
                  startdate: sl.date,
                })}
              >
                {sl.time.format(timeFormat) +
                  " - " +
                  sl.time.plusMinutes(sl.duration).format(timeFormat) +
                  " (" +
                  "vanaf" +
                  " " +
                  sl.date.format(dateFormat) +
                  ")"}
              </a>
            ))}
        </div>
      </div>
    );
  }
}

function renderPrivateLessonsFormulas(
  mode: {
    tag: "privatelessonsformula";
    site: siteChoice;
    instrument: instrumentid;
    internalteacherid: internalteacherid | null;
  },
  allTeachers: teacherForPublicPlanning[],
  allFormulas: formula[],
  instruments: { id: instrumentid; description: string }[],
) {
  const siteFilter = mkSiteFilter(mode.site);
  const filteredPrivateFormulas = allFormulas
    .filter(
      (f) =>
        // Only private formulas are relevant
        f.type_.startsWith("Private") &&
        f.planningmode === "Weekly" &&
        // Only those we want so sho
        f.showonpublicplanning === true &&
        // Only those with correct instrument
        f.instruments.some((i) => i.instrumentid === mode.instrument),
    )
    .map((f) => {
      const allSlots: freeSlot[] = getSlotsForFormula(
        mode,
        allTeachers,
        f,
      ).filter(siteFilter);
      return { ...f, valid: allSlots.length > 0 };
    })
    .orderBy(
      [(f) => (f.valid ? 1 : -1), (f) => f.description],
      ["desc", "asc"],
    );
  const formulasRendered = filteredPrivateFormulas.map((form) => {
    const link = makeHashLink({
      ...mode,
      tag: "privatelessonscalendar" as const,
      formulaid: form.id,
    });

    const a = (
      <a
        className={style.card}
        style={
          "margin-bottom: 8px; display: block;" +
          (form.valid ? "" : invalidCardStyle)
        }
      >
        <div className={style.text_with_icon}>
          <i className={FA.icon_music_solid}></i>
          <div
            style={
              "text-decoration: none; " +
              (form.valid ? "font-weight: 600;" : "")
            }
          >
            {form.description}
          </div>
        </div>
        <div className={style.text_with_icon}>
          <i className={FA.icon_clock_regular}></i>
          <div style="text-decoration: none;">
            {form.numberoflessons +
              " " +
              "Lessen".toLowerCase() +
              " | " +
              form.lessonduration +
              "min"}
          </div>
        </div>
      </a>
    );
    if (form.valid) {
      a.setAttribute("href", link);
    }

    return a;
  });

  return (
    <main style={mainStyle}>
      <article>
        <h2>{renderHeader(instruments, mode)}</h2>
      </article>

      <div>
        <div className={""}>{formulasRendered}</div>
      </div>
    </main>
  );
}

function getSlotsForFormula(
  mode: {
    site: siteChoice;
    instrument: instrumentid;
    internalteacherid: null | number;
  },
  allTeachers: teacherForPublicPlanning[],
  formula: { lessonduration: number; numberoflessons: number },
): freeSlot[] {
  const siteFilter = mkSiteFilter(mode.site);
  const allSlots_: freeSlot[] =
    mode.internalteacherid === null
      ? allTeachers
          .filter(
            (t) =>
              t.allTeachingPeriods.some(siteFilter) &&
              t.instruments.includes(mode.instrument),
          )
          .flatMap((t) =>
            t.freeSlotsThisWeek
              .concat(t.freeSlotsNextWeek)
              .concat(t.freeSlotsThirdWeek)
              .filter(siteFilter)
              // for debugging
              .map((sl) => ({
                ...sl,
                name: renderFirstNameLastName(t),
                internalteacherid: t.internalteacherid,
              })),
          )
      : (function () {
          const t = allTeachers.find(
            (t) => t.internalteacherid === mode.internalteacherid,
          );
          if (t) {
            return (
              t.freeSlotsThisWeek
                .concat(t.freeSlotsNextWeek)
                .concat(t.freeSlotsThirdWeek)
                // for debugging
                .map((sl) => ({
                  ...sl,
                  name: renderFirstNameLastName(t),
                  internalteacherid: t.internalteacherid,
                }))
            );
          } else {
            // Error
            return [];
          }
        })();

  return allSlots_
    .filter((sl) =>
      sl.freeForSingleLessonOnly ? formula.numberoflessons === 1 : true,
    )
    .flatMap((sl) => split(sl, formula.lessonduration))
    .uniqBy((sl) => sl.weekday.ordinal().toString() + sl.time.format(HHmm));

  function split(f: freeSlot, lessonduration: number): freeSlot[] {
    if (f.duration < lessonduration) {
      // Too little space
      return [];
    } else {
      return [{ ...f, duration: lessonduration }].concat(
        split(
          {
            ...f,
            time: f.time.plusMinutes(lessonduration),
            duration: f.duration - lessonduration,
          },
          lessonduration,
        ),
      );
    }
  }
}

function mkSiteFilter(
  siteChoice: siteChoice,
): (s: { siteid: number | null }) => boolean {
  return function siteFilter(s: { siteid: number | null }) {
    return (
      siteChoice.tag === "nopreference" ||
      (siteChoice.tag === "preference" &&
        (siteChoice.siteId === s.siteid || s.siteid === null))
    );
  };
}

function mkPrivateFormulaFilter(
  allFormulas: formula[],
  instrumentid: instrumentid,
): (s: freeSlot) => boolean {
  const privateFormulasForInstrument = allFormulas.filter(
    (f) =>
      f.showonpublicplanning &&
      f.planningmode !== "Manual" &&
      f.instruments.some((id) => id.instrumentid === instrumentid),
  );
  return function privateFormulaFilter(s: freeSlot) {
    return privateFormulasForInstrument.some(
      (pf) => pf.lessonduration <= s.duration,
    );
  };
}
